import DynamicDialog from '@/components/DynamicDialog';
import { useLoginUser, usePasswordLostMutation, useReloadLicenseData } from '@http/queries.js';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { IconEye, IconEyeOff, IconLogin, IconMail } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function LoginForm() {
    const [error, setError] = useState();
    const [lostPwError, setLostPwError] = useState();
    const [lostPwSuccess, setLostPwSuccess] = useState();

    const { t } = useTranslation();

    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [pwLostDialogOpen, setPwLostDialogOpen] = useState(false);

    const loginMutation = useLoginUser();

    const pwLostMutation = usePasswordLostMutation();

    const reloadLicenseData = useReloadLicenseData();

    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });

    const [errors, setErrors] = useState({
        email: '',
        password: ''
    });

    function handleChange(e) {
        const { name, value } = e.target;

        setFormData(prevFormData => {
            const newFormData = { ...prevFormData, [name]: value };
            // Directly validate form after state update
            validateForm(name, newFormData);
            return newFormData;
        });
    }

    function validateEmail(email) {
        if (!email) return t('emailRequired');

        if (!email.includes('@')) return t('emailInvalid');

        return '';
    }

    function validateForm(fieldName, newFormData) {
        let valid = true;
        let tempErrors = { ...errors };

        if (!newFormData) newFormData = formData;

        const password = newFormData.password.trim();
        const email = newFormData.email.trim();

        // fix for browser autofill, force validation of both fields
        if (password && email) fieldName = '';

        if (fieldName === 'email' || !fieldName) {
            const error = validateEmail(email);
            if (error) {
                valid = false;
                tempErrors.email = error;
            } else {
                tempErrors.email = '';
            }
        }

        if (fieldName === 'password' || !fieldName) {
            if (!password) {
                valid = false;
                tempErrors.password = t('passwordRequired');
            } else {
                tempErrors.password = '';
            }
        }

        setErrors(tempErrors);
        return valid;
    }

    async function handleSubmit(e) {
        e.preventDefault();

        setSubmitting(true);

        if (validateForm()) {
            console.log('Form submitted:', formData);

            const password = formData.password.trim();
            const email = formData.email.trim();

            try {
                const user = await loginMutation.mutateAsync({
                    password,
                    email
                });

                console.log('Login successful', user);
                // show boot screen again
                document.body.style.overflow = 'hidden';
                document.querySelector('#boot-screen').style.display = 'flex';

                await reloadLicenseData();
            } catch (error) {
                console.error('Login error:', error?.message || error);
                setError(t('loginFailedInvalidCredentials'));
            }
        }

        setSubmitting(false);
    }

    async function handleSubmitForgotPassword(e) {
        e.preventDefault();

        setSubmitting(true);

        const emailError = validateEmail(formData.email);

        if (emailError) {
            setErrors({ email: emailError });
            setSubmitting(false);
            return;
        }

        const email = formData.email.trim();

        console.log('PW Lost Form submitted:', email);

        try {
            const user = await pwLostMutation.mutateAsync({
                email
            });

            console.log('PW Lost successful', user);

            setLostPwSuccess(t('passwordForgotSuccess'));
        } catch (error) {
            console.error('PW Lost error:', error);
            setLostPwError(t('errorTryAgain'));
        }

        setSubmitting(false);
    }

    return (
        <>
            <DynamicDialog
                overwriteOpen={pwLostDialogOpen}
                onCancel={() => setPwLostDialogOpen(false)}
                title={t('forgotPassword')}
                hideActions
                hideActivator
                maxWidth='sm'
            >
                <form onSubmit={handleSubmitForgotPassword}>
                    <Typography>{t('passwordForgotDescription')}</Typography>

                    {lostPwError ? (
                        <Typography color='#b60000' my={2} variant='h3'>
                            {lostPwError}
                        </Typography>
                    ) : null}

                    {lostPwSuccess ? (
                        <Typography color='#00b600' my={2} variant='h3'>
                            {lostPwSuccess}
                        </Typography>
                    ) : null}

                    <TextField
                        label={t('email')}
                        autoComplete='email'
                        type='email'
                        autoFocus
                        error={!!errors.email}
                        fullWidth
                        helperText={errors.email}
                        id='email'
                        margin='normal'
                        name='email'
                        onBlur={() => validateForm('email')}
                        onChange={handleChange}
                        onInput={handleChange}
                        sx={{
                            mt: 3
                        }}
                        value={formData.email}
                        variant='outlined'
                    />

                    <LoadingButton
                        sx={{ mt: 3 }}
                        fullWidth
                        loading={submitting}
                        startIcon={<IconMail />}
                        type='submit'
                        variant='contained'
                        size='large'
                    >
                        {t('requestLink')}
                    </LoadingButton>
                </form>
            </DynamicDialog>

            <form onSubmit={handleSubmit}>
                <Box fontWeight={600}>{t('email')}:</Box>

                <TextField
                    autoComplete='email'
                    type='email'
                    autoFocus
                    error={!!errors.email}
                    fullWidth
                    helperText={errors.email}
                    id='email'
                    margin='normal'
                    name='email'
                    onBlur={() => validateForm('email')}
                    onChange={handleChange}
                    onInput={handleChange}
                    sx={{
                        mt: 0.75
                    }}
                    value={formData.email}
                    variant='outlined'
                />

                <Box fontWeight={600} mt={2}>
                    {t('passwordOrKey')}:
                </Box>

                <TextField
                    autoComplete='current-password'
                    error={!!errors.password}
                    fullWidth
                    helperText={errors.password}
                    id='password'
                    margin='normal'
                    name='password'
                    onBlur={() => validateForm('password')}
                    onChange={handleChange}
                    onInput={handleChange}
                    sx={{
                        mt: 0.75,
                        mb: 2
                    }}
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    variant='outlined'
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position='end'>
                                <Tooltip
                                    title={showPassword ? t('hidePassword') : t('showPassword')}
                                    placement='top'
                                >
                                    <IconButton onClick={() => setShowPassword(prev => !prev)}>
                                        {showPassword ? <IconEyeOff /> : <IconEye />}
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        )
                    }}
                />

                {/* <Box sx={{ textDecoration: 'underline' }}>{t('forgotPassword')}</Box> */}

                <Button variant='text' size='small' onClick={() => setPwLostDialogOpen(true)}>
                    {t('forgotPassword')}
                </Button>

                {error ? (
                    <Typography color='#b60000' mt={4} mb={2} variant='h3'>
                        {error}
                    </Typography>
                ) : null}

                <LoadingButton
                    sx={{ mt: 4 }}
                    fullWidth
                    loading={submitting}
                    startIcon={<IconLogin />}
                    type='submit'
                    variant='contained'
                    size='large'
                    /*sx={{
                        background: buttonGradient
                    }}*/
                >
                    {t('signIn')}
                </LoadingButton>
            </form>
        </>
    );
}
